import React, { useState, useEffect } from 'react'
import { XMarkIcon, SparklesIcon, PencilIcon, GiftIcon, CheckIcon } from '@heroicons/react/24/outline'
import { useLocation } from 'react-router-dom'

const STORAGE_KEY = 'twiggle_discount_popup_dismissed'

const DiscountPopup = () => {
  const [visible, setVisible] = useState(false)
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const location = useLocation()

  const isShopOrProduct = location.pathname.startsWith('/shop')

  useEffect(() => {
    if (isShopOrProduct && !localStorage.getItem(STORAGE_KEY)) {
      const timer = setTimeout(() => setVisible(true), 800)
      return () => clearTimeout(timer)
    }
  }, [isShopOrProduct])

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1')
    setVisible(false)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!email.trim()) return
    setSubmitted(true)
    localStorage.setItem(STORAGE_KEY, '1')
    localStorage.setItem('twiggle_claimed_first_order_discount', 'true')
    window.dispatchEvent(new Event('storage'))
    setTimeout(() => setVisible(false), 2000)
  }

  if (!visible) return null

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 bg-slate-900/60 z-[60] backdrop-blur-sm transition-opacity duration-300"
        onClick={dismiss}
        aria-hidden="true"
      />
      
      {/* Premium Modal Dialog */}
      <div
        role="dialog"
        aria-label="Exclusive Clinic and Bulk Offers"
        className="fixed left-1/2 top-1/2 z-[61] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 sm:p-8 shadow-2xl border border-emerald-100 transition-all duration-300 transform scale-100"
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={dismiss}
          className="absolute right-4 top-4 rounded-lg p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-colors"
          aria-label="Close dialog"
        >
          <XMarkIcon className="h-5.5 w-5.5" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-800 text-xs font-bold tracking-wide uppercase shadow-sm">
            <SparklesIcon className="w-3.5 h-3.5" />
            Clinic Bulk Program
          </div>
          <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight leading-tight">
            Exclusive Clinic Offers
          </h2>
          <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto leading-relaxed">
            Maximize your practice's budget with our professional sustainable oral care programs.
          </p>
        </div>

        {/* Offers Cards */}
        <div className="space-y-3 mb-6">
          
          {/* Engraving Promo Card */}
          <div className="flex gap-4 p-4 rounded-xl border border-emerald-600/10 bg-emerald-50/40 hover:bg-emerald-50/60 transition-colors duration-200">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shadow-sm">
              <PencilIcon className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-bold text-gray-900 text-sm sm:text-base">Free Custom Engraving</h4>
              <p className="text-gray-600 text-xs sm:text-sm mt-0.5 leading-relaxed">
                Buy any <strong>5 boxes</strong> and receive free engraving on your order (mix &amp; match allowed).
              </p>
            </div>
          </div>

          {/* Complimentary Box Promo Card */}
          <div className="flex gap-4 p-4 rounded-xl border border-amber-600/10 bg-amber-50/40 hover:bg-amber-50/60 transition-colors duration-200">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shadow-sm">
              <GiftIcon className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-bold text-gray-900 text-sm sm:text-base">Complimentary Box</h4>
              <p className="text-gray-600 text-xs sm:text-sm mt-0.5 leading-relaxed">
                Buy <strong>7 boxes</strong> and receive <strong>1 complimentary box</strong> of Regular White Ultrasoft Bamboo Toothbrushes.
              </p>
            </div>
          </div>

        </div>

        {/* Newsletter Signup (10% off box) */}
        <div className="border-t border-gray-100 pt-5">
          {submitted ? (
            <div className="py-4 text-center">
              <p className="text-lg font-bold text-emerald-700 flex items-center justify-center gap-1.5">
                <CheckIcon className="w-5 h-5" /> Discount Claimed!
              </p>
              <p className="mt-1.5 text-sm text-gray-600">Your additional 10% discount will be applied at checkout.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="flex flex-col sm:flex-row items-center gap-2">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="clinic-email@practice.com"
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 outline-none transition-all"
                  aria-label="Clinic email for discount"
                />
                <button
                  type="submit"
                  className="w-full sm:w-auto sm:flex-shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-6 py-3 rounded-xl shadow-md transition-colors"
                >
                  Claim +10% Off
                </button>
              </div>
              <p className="text-center text-[10px] text-gray-400 leading-tight">
                Claim 10% off your first box. Can be combined with complimentary shipping on bulk orders.
              </p>
            </form>
          )}
        </div>

        {/* Start Shopping Action */}
        <div className="mt-4 text-center">
          <button
            onClick={dismiss}
            className="text-xs text-gray-400 font-semibold hover:text-emerald-700 underline transition-colors"
          >
            No thanks, just let me shop
          </button>
        </div>

      </div>
    </>
  )
}

export default DiscountPopup
